import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowRight, Flag, ShieldAlert } from "lucide-react";
import { useCountry } from "@/context/CountryContext";
import { theftStatsFor } from "@/data/theftStats";
import { regionalTheftFor, formatStolen } from "@/data/regionalTheftStats";

/**
 * Short, scary, source-backed urgency panel. Links out to the full theft
 * statistics page. Content auto-swaps between Australia and the United States
 * based on the shopper's detected (or manually chosen) market so Americans
 * see US-centric numbers and Aussies see AU numbers.
 *
 * Geo-aware headline: when the backend geo lookup resolves an ip-api region
 * (e.g. "Queensland", "California"), we replace the national headline with a
 * state-specific line that reads *"X,XXX vehicles stolen in {state} last year."*
 * This is a conversion lifter — local numbers feel more real than national ones.
 * Falls back to the national headline when region is unknown or we don't have
 * data for that state.
 */
const TheftUrgency = () => {
  const { isUS, code, region } = useCountry();
  const s = theftStatsFor(isUS);
  const route = s.canonical;
  const unrecoveredArc = s.donut.percent;
  // Regional override — only kicks in when we have stats for the detected state.
  const regional = regionalTheftFor(code, region);
  const headline = regional
    ? `${formatStolen(regional.stolen)} vehicles stolen in ${regional.name} last year.`
    : s.headline;
  return (
    <section
      data-testid="theft-urgency-section"
      data-country={isUS ? "US" : "AU"}
      data-region={regional ? regional.name : "national"}
      className="relative overflow-hidden bg-tracksy-ink py-16 text-white sm:py-20"
    >
      {/* Background texture */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(135deg, #ff6b1a 0 2px, transparent 2px 14px)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-tracksy-orange/60 to-transparent"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-tracksy-orange/60 to-transparent"
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 sm:px-6 lg:grid-cols-[1.1fr_1fr] lg:items-center lg:gap-14 lg:px-8">
        {/* LEFT: Copy */}
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-tracksy-orange/15 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-tracksy-orange ring-1 ring-tracksy-orange/40">
            <Flag className="h-3.5 w-3.5" /> {regional ? `${regional.name} · 2024–25` : s.flagLabel}
          </div>
          <h2 className="mt-5 font-heading text-3xl font-black leading-[1.08] tracking-tight sm:text-4xl lg:text-5xl">
            {headline}
            <br />
            <span className="text-tracksy-orange">{s.subhead}</span>
          </h2>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-white/80 sm:text-base">
            {s.bodyCopy}
          </p>

          {/* Mini stat row */}
          <div className="mt-7 grid grid-cols-3 gap-3 sm:gap-5">
            {s.bricks.map((b) => (
              <StatBrick key={b.small} big={b.big} small={b.small} />
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to={route}
              data-testid="theft-urgency-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-tracksy-orange px-6 py-3 text-sm font-black uppercase tracking-widest text-white shadow-glow transition-colors hover:bg-tracksy-orangeHover"
            >
              See the numbers <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/shop"
              data-testid="theft-urgency-shop-cta"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-5 py-3 text-sm font-black uppercase tracking-widest text-white backdrop-blur transition-colors hover:bg-white/10"
            >
              Protect yours <ShieldAlert className="h-4 w-4" />
            </Link>
          </div>
          <p className="mt-4 text-[11px] font-semibold uppercase tracking-widest text-white/50">
            {s.sourcesLabel}
          </p>
        </div>

        {/* RIGHT: Simple radial "unrecovered" visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto flex aspect-square w-full max-w-[380px] items-center justify-center"
        >
          {/* Ring */}
          <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
            <defs>
              <linearGradient id="recoveryGrad" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0" stopColor="#ff6b1a" />
                <stop offset="1" stopColor="#ff4b1a" />
              </linearGradient>
            </defs>
            <circle cx="60" cy="60" r="48" fill="none" stroke="#2A2A2A" strokeWidth="10" />
            <motion.circle
              cx="60"
              cy="60"
              r="48"
              fill="none"
              stroke="url(#recoveryGrad)"
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={`${(unrecoveredArc / 100) * (2 * Math.PI * 48)} ${(2 * Math.PI * 48)}`}
              initial={{ strokeDashoffset: 2 * Math.PI * 48 }}
              whileInView={{ strokeDashoffset: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <AlertTriangle className="mb-2 h-6 w-6 text-tracksy-orange" />
            <div className="font-heading text-5xl font-black leading-none text-white sm:text-6xl">{unrecoveredArc}%</div>
            <div className="mt-2 max-w-[180px] text-[11px] font-black uppercase tracking-widest text-white/80">
              {s.donut.label}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const StatBrick = ({ big, small }) => (
  <div className="rounded-2xl border border-white/10 bg-white/5 px-3 py-4 backdrop-blur">
    <div className="font-heading text-xl font-black leading-none text-tracksy-orange sm:text-2xl">{big}</div>
    <div className="mt-2 text-[10px] font-bold uppercase leading-tight tracking-widest text-white/70">{small}</div>
  </div>
);

export default TheftUrgency;
